import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Mail, Trash2, LogOut, User, ArrowLeft, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_videogist-3/artifacts/809w15is_summbee%20logo_just%20bee.png";

function WorkspaceDetailPage() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const [workspace, setWorkspace] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteUrl, setInviteUrl] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchWorkspaceDetails();
    fetchMembers();
  }, [workspaceId]);

  const fetchWorkspaceDetails = async () => {
    try {
      const response = await axios.get(`${API}/workspaces`, { withCredentials: true });
      const ws = response.data.workspaces.find(w => w.id === workspaceId);
      setWorkspace(ws);
    } catch (error) {
      console.error("Error fetching workspace:", error);
      toast.error("Failed to load workspace");
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/workspaces/${workspaceId}/members`, { withCredentials: true });
      setMembers(response.data.members || []);
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    setInviting(true);
    try {
      const response = await axios.post(
        `${API}/workspaces/${workspaceId}/invite`,
        { email: inviteEmail },
        { withCredentials: true }
      );
      toast.success("Invite sent!");
      setInviteUrl(response.data.invite_url);
      setInviteEmail("");
    } catch (error) {
      console.error("Error sending invite:", error);
      toast.error(error.response?.data?.detail || "Failed to send invite");
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (memberUserId) => {
    if (!window.confirm("Remove this member from the workspace?")) {
      return;
    }

    try {
      await axios.delete(
        `${API}/workspaces/${workspaceId}/members/${memberUserId}`,
        { withCredentials: true }
      );
      toast.success("Member removed");
      fetchMembers();
      fetchWorkspaceDetails();
    } catch (error) {
      console.error("Error removing member:", error);
      toast.error(error.response?.data?.detail || "Failed to remove member");
    }
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    toast.success("Invite link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading || !workspace) {
    return (
      <div className="dashboard-page">
        <div className="loading-state">Loading...</div>
      </div>
    );
  }

  const isOwner = workspace.role === "owner";

  return (
    <div className="dashboard-page">
      <nav className="navbar">
        <div className="nav-content">
          <div className="nav-logo" onClick={() => navigate("/")} style={{cursor: "pointer"}}>
            <img src={LOGO_URL} alt="SummBee" className="logo-image" />
            <span>SummBee</span>
          </div>
          <div className="nav-links">
            <Button onClick={() => navigate("/workspaces")} variant="ghost">
              <ArrowLeft size={16} /> Back to Workspaces
            </Button>
            <div className="user-info">
              <User size={16} />
              <span>{user?.name}</span>
            </div>
            <Button onClick={logout} variant="ghost" className="logout-button">
              <LogOut size={16} /> Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="dashboard-container">
        <div className="page-header">
          <div>
            <h1 className="page-title">{workspace.name}</h1>
            <p className="page-subtitle">{workspace.member_count} members</p>
          </div>
          {isOwner && (
            <Button onClick={() => setShowInviteModal(true)} className="create-button">
              <Mail size={20} /> Invite Members
            </Button>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="card-title">
              <Users className="card-icon" />
              Members
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="members-list">
              {members.map((member) => (
                <div key={member.user_id} className="member-item">
                  <div className="member-info">
                    <div className="member-avatar">
                      {member.user?.picture ? (
                        <img src={member.user.picture} alt={member.user.name} />
                      ) : (
                        <User size={24} />
                      )}
                    </div>
                    <div className="member-details">
                      <div className="member-name">{member.user?.name}</div>
                      <div className="member-email">{member.user?.email}</div>
                    </div>
                    {member.role === "owner" && (
                      <span className="owner-badge">Owner</span>
                    )}
                  </div>
                  {isOwner && member.role !== "owner" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveMember(member.user_id)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay">
          <div className="modal-content invite-modal">
            <div className="modal-header">
              <h2>Invite Members</h2>
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteUrl("");
                  setInviteEmail("");
                }}
                className="close-button"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {inviteUrl ? (
                <div className="invite-success">
                  <p className="success-message">✓ Invite created!</p>
                  <div className="invite-link-container">
                    <Input value={inviteUrl} readOnly />
                    <Button onClick={copyInviteLink} variant="outline">
                      {copied ? <Check size={16} /> : <Copy size={16} />}
                    </Button>
                  </div>
                  <p className="invite-hint">Share this link with your colleague to join the workspace</p>
                  <Button onClick={() => {
                    setInviteUrl("");
                    setInviteEmail("");
                  }}>
                    Send Another Invite
                  </Button>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label>Email Address</label>
                    <Input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="colleague@company.com"
                    />
                  </div>
                  <Button
                    onClick={handleSendInvite}
                    disabled={inviting}
                    className="save-button"
                  >
                    {inviting ? "Sending..." : "Send Invite"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkspaceDetailPage;
